import { useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Reveal, SectionHead } from "../motion";
import { PrivacyNotice } from "./PrivacyNotice";
import { SERVICE_OPTIONS } from "../../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", organisation: "", service: "", message: "", website: "" };

function validate(form) {
  const errors = {};
  if (form.name.trim().length < 2) errors.name = "Please enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Please enter a valid email address.";
  if (form.message.trim().length < 20) errors.message = "Please tell me a little more (at least 20 characters).";
  return errors;
}

const inputClass =
  "w-full rounded-sm border border-ink/15 bg-warmwhite px-4 py-3 text-base text-ink placeholder:text-ink/40 transition-colors focus:border-cherry focus:outline-none sm:text-sm";

export function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setStatus("success");
      setForm(initialForm);
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20 lg:py-24">
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <div>
          <SectionHead
            eyebrow="Contact"
            title="Get in Touch"
            intro="If you need help with an AML review, compliance documentation or ongoing support, I&rsquo;d be happy to discuss your requirements."
          />
          <Reveal delay={0.15}>
            <div className="mt-6 space-y-3 text-sm leading-relaxed text-ink/65">
              <p className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                A few lines about your business and what you need is enough to begin.
              </p>
              <p className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                Please don&rsquo;t send sensitive client documents through this form.
              </p>
              <p className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                Your details are used only to respond to you.{" "}
                <PrivacyNotice
                  testid="contact-privacy-button"
                  triggerClassName="font-medium text-cherry underline underline-offset-4 hover:text-cherry-deep"
                />
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-sm border border-ink/10 bg-warmwhite p-6 shadow-[0_24px_60px_-40px_rgba(123,30,58,0.4)] sm:p-10">
            {status === "success" ? (
              <div data-testid="contact-success" role="status" className="flex flex-col items-start gap-4 py-10">
                <CheckCircle2 className="h-10 w-10 text-cherry" aria-hidden="true" />
                <h3 className="font-display text-2xl text-ink">Thank you, your enquiry has been sent.</h3>
                <p className="text-sm leading-relaxed text-ink/65">
                  Your message has been sent and I will read it personally. I&rsquo;ll come back to you as soon as I can.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another-button"
                  onClick={() => setStatus("idle")}
                  className="mt-2 rounded-full border border-ink/25 px-6 py-2.5 text-sm font-medium text-ink transition-colors hover:border-cherry hover:text-cherry"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink/60">
                      Name *
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={update("name")}
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? "contact-name-error" : undefined}
                      className={inputClass}
                      placeholder="Your full name"
                    />
                    {errors.name && (
                      <p id="contact-name-error" data-testid="contact-name-error" role="alert" className="mt-1.5 text-xs text-cherry">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink/60">
                      Email *
                    </label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={update("email")}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? "contact-email-error" : undefined}
                      className={inputClass}
                      placeholder="you@firm.com"
                    />
                    {errors.email && (
                      <p id="contact-email-error" data-testid="contact-email-error" role="alert" className="mt-1.5 text-xs text-cherry">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="contact-organisation" className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink/60">
                      Organisation
                    </label>
                    <input
                      id="contact-organisation"
                      data-testid="contact-organisation-input"
                      type="text"
                      autoComplete="organization"
                      value={form.organisation}
                      onChange={update("organisation")}
                      className={inputClass}
                      placeholder="Firm or team (optional)"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-service" className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink/60">
                      How can I help?
                    </label>
                    <select
                      id="contact-service"
                      data-testid="contact-service-select"
                      value={form.service}
                      onChange={update("service")}
                      className={inputClass}
                    >
                      <option value="">Choose an area (optional)</option>
                      {SERVICE_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs font-medium uppercase tracking-[0.16em] text-ink/60">
                    Message *
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? "contact-message-error" : undefined}
                    className={`${inputClass} resize-y`}
                    placeholder="A short outline of your business and what you need."
                  />
                  {errors.message && (
                    <p id="contact-message-error" data-testid="contact-message-error" role="alert" className="mt-1.5 text-xs text-cherry">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* Honeypot, hidden from humans, bots fill it */}
                <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    data-testid="contact-honeypot-input"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                  />
                </div>

                {status === "error" && (
                  <p data-testid="contact-error" role="alert" className="mt-4 rounded-sm border border-cherry/30 bg-blush/40 px-4 py-3 text-sm text-cherry">
                    Something went wrong sending your enquiry. Please try again in a moment.
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "sending"}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cherry px-8 py-3 text-sm font-medium text-ivory transition-colors duration-200 hover:bg-cherry-deep disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send enquiry
                      <Send className="h-4 w-4" aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
